import { useEffect } from 'react';
import { BackHandler, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { tokens } from '../../tokens';
import { Button } from '../button';
import { HeaderFrame } from '../header/header-frame';
import { HeaderTitleText } from '../header/header-title-text';
import { LogoSvg } from '../logo';
import { Text } from '../text';

const DEFAULT_TITLE = 'Age Restriction';
const DEFAULT_HEADLINE = 'Unable to Access App';
const DEFAULT_BODY =
  'According to state law, guests under a certain age are prohibited from using this app without permission from a parent or guardian.';
const DEFAULT_CLOSE_APP_LABEL = 'Close App';

export type AgeRestrictionBlockProps = {
  onCloseApp: () => void;
  title?: string;
  headline?: string;
  body?: string;
  closeAppLabel?: string;
};

export function AgeRestrictionBlock({
  onCloseApp,
  title = DEFAULT_TITLE,
  headline = DEFAULT_HEADLINE,
  body = DEFAULT_BODY,
  closeAppLabel = DEFAULT_CLOSE_APP_LABEL,
}: AgeRestrictionBlockProps) {
  useEffect(() => {
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      onCloseApp();
      return true;
    });
    return () => subscription.remove();
  }, [onCloseApp]);

  return (
    <SafeAreaView style={styles.safeArea} edges={['bottom']} testID="age-restriction-block">
      <HeaderFrame
        style={styles.header}
        accessibilityRole="header"
        accessibilityLabel={title}
      >
        <HeaderFrame.Middle layout="inline">
          <HeaderTitleText text={title} />
        </HeaderFrame.Middle>
      </HeaderFrame>

      <View style={styles.content}>
        <LogoSvg
          height={54}
          accessibilityLabel="Firehouse Subs"
          importantForAccessibility="no-hide-descendants"
        />
        <View
          accessible
          accessibilityRole="summary"
          accessibilityLabel={`${headline}. ${body}`}
          style={styles.copy}
        >
          <Text.Heading type="one" style={styles.headline} accessible={false}>
            {headline}
          </Text.Heading>
          <Text.Paragraph size="md" style={styles.body} accessible={false}>
            {body}
          </Text.Paragraph>
        </View>
        <Button
          type="solid"
          size="xl"
          style={styles.button}
          onPress={onCloseApp}
          accessibilityRole="button"
          accessibilityLabel={closeAppLabel}
          testID="age-restriction-close-app"
        >
          <Button.Text>{closeAppLabel}</Button.Text>
        </Button>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: tokens.colors.$white,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.$blackOpacity10,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 48,
  },
  copy: {
    alignItems: 'center',
    marginTop: 32,
    gap: 12,
  },
  headline: {
    textAlign: 'center',
  },
  body: {
    textAlign: 'center',
  },
  button: {
    alignSelf: 'stretch',
    marginTop: 32,
  },
});
